import React, { useState } from "react";
import axios from "axios";
import { FormFeedback } from "reactstrap";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl, getMediaUrl } from "../../../utils/site";

const AddProduct = ({ onProductAdded }) => {
  const [product, setProduct] = useState({
    name: "",
    category: "",
    price: "",
    stock_quantity: "0",
    description: "",
    image_url: "",
    material: "",
    weight: "",
    size: "",
    status: "active",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageSource, setImageSource] = useState("url");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleImageUrlChange = (e) => {
    setProduct((prev) => ({ ...prev, image_url: e.target.value }));
    setImagePreview(e.target.value ? e.target.value : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    const nextErrors = {};
    if (!product.name.trim()) nextErrors.name = "Product name is required.";
    if (!product.price || isNaN(product.price) || Number(product.price) < 0) nextErrors.price = "Price must be a non-negative number.";
    const stockQuantity = parseInt(product.stock_quantity, 10);
    if (product.stock_quantity === "" || isNaN(stockQuantity) || stockQuantity < 0) nextErrors.stock_quantity = "Stock quantity must be a non-negative integer.";
    if (imageSource === "url" && !product.image_url.trim()) nextErrors.image_url = "Provide an image URL or choose upload image.";
    if (imageSource === "upload" && !imageFile) nextErrors.image = "Upload an image file or choose image URL.";
    if (product.weight && (isNaN(product.weight) || Number(product.weight) < 0)) nextErrors.weight = "Weight must be a non-negative number.";

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setIsSubmitting(false);
      return;
    }

    const token = localStorage.getItem("token");
    try {
      if (imageSource === "upload" && imageFile) {
        const formData = new FormData();
        formData.append("stock_quantity", String(stockQuantity));
        Object.entries(product).forEach(([key, value]) => {
          if (key !== "image_url" && key !== "stock_quantity" && value !== "") {
            formData.append(key, ["price", "weight"].includes(key) ? Number(value) : value);
          }
        });
        formData.append("image", imageFile);

        await axios.post(getApiUrl("/api/products"), formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(
          getApiUrl("/api/products"),
          {
            ...product,
            price: Number(product.price),
            stock_quantity: stockQuantity,
            weight: product.weight ? Number(product.weight) : null,
          },
          {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          }
        );
      }

      toast.success("Product added successfully.");
      setProduct({
        name: "",
        category: "",
        price: "",
        stock_quantity: "",
        description: "",
        image_url: "",
        material: "",
        weight: "",
        size: "",
        status: "active",
      });
      setImageFile(null);
      setImagePreview(null);
      setImageSource("url");
      if (onProductAdded) onProductAdded();
    } catch (error) {
      if (error.response?.data?.errors) {
        const backendErrors = {};
        error.response.data.errors.forEach((err) => {
          backendErrors[err.path || err.param] = err.msg;
        });
        setErrors(backendErrors);
        toast.error("Please fix the validation errors.");
      } else {
        toast.error(error.response?.data?.error || `Failed to add product: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Add New Product | Roha Jewellery Admin</title>
      </Helmet>
      <AdminPageShell
        eyebrow="Catalogue creation"
        title="Add Product"
        description="Create a premium product entry with pricing, stock, materials, and image support in one clean flow."
      >
        <AdminSurface title="Product composer" subtitle="Build the next catalogue item">
          <form className="roha-admin-form-grid" onSubmit={handleSubmit}>
            <div className="roha-admin-form-grid roha-admin-form-grid--two">
              <div className="roha-admin-field">
                <label>Jewellery name</label>
                <input className="form-control" name="name" value={product.name} onChange={handleChange} />
                <FormFeedback style={{ display: errors.name ? "block" : "none" }}>{errors.name}</FormFeedback>
              </div>
              <div className="roha-admin-field">
                <label>Category</label>
                <input className="form-control" name="category" value={product.category} onChange={handleChange} />
              </div>
            </div>
            <div className="roha-admin-form-grid roha-admin-form-grid--two">
              <div className="roha-admin-field">
                <label>Price (ETB)</label>
                <input type="number" className="form-control" name="price" value={product.price} onChange={handleChange} />
              </div>
              <div className="roha-admin-field">
                <label>Stock quantity</label>
                <input type="number" className="form-control" name="stock_quantity" value={product.stock_quantity} onChange={handleChange} />
              </div>
            </div>
            <div className="roha-admin-form-grid roha-admin-form-grid--two">
              <div className="roha-admin-field">
                <label>Material</label>
                <input className="form-control" name="material" value={product.material} onChange={handleChange} />
              </div>
              <div className="roha-admin-field">
                <label>Weight (g)</label>
                <input type="number" className="form-control" name="weight" value={product.weight} onChange={handleChange} />
              </div>
            </div>
            <div className="roha-admin-form-grid roha-admin-form-grid--two">
              <div className="roha-admin-field">
                <label>Size</label>
                <input className="form-control" name="size" value={product.size} onChange={handleChange} />
              </div>
              <div className="roha-admin-field">
                <label>Status</label>
                <select className="form-select" name="status" value={product.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="roha-admin-toolbar">
              <label className="roha-admin-pill">
                <input
                  type="radio"
                  checked={imageSource === "url"}
                  onChange={() => {
                    setImageSource("url");
                    setImageFile(null);
                    setImagePreview(product.image_url);
                  }}
                />
                Image URL
              </label>
              <label className="roha-admin-pill">
                <input
                  type="radio"
                  checked={imageSource === "upload"}
                  onChange={() => {
                    setImageSource("upload");
                    setProduct((prev) => ({ ...prev, image_url: "" }));
                    setImagePreview(null);
                  }}
                />
                Upload image
              </label>
            </div>
            {imageSource === "url" ? (
              <div className="roha-admin-field">
                <label>Image URL</label>
                <input className="form-control" value={product.image_url} onChange={handleImageUrlChange} />
              </div>
            ) : (
              <div className="roha-admin-field">
                <label>Upload image</label>
                <input type="file" className="form-control" accept="image/*" onChange={handleFileChange} />
              </div>
            )}
            {imagePreview ? (
              <img src={getMediaUrl(imagePreview)} alt="Preview" className="roha-admin-image-thumb" style={{ width: 180, height: 180 }} />
            ) : null}
            <div className="roha-admin-field">
              <label>Description</label>
              <textarea className="form-control" name="description" rows="5" value={product.description} onChange={handleChange} />
            </div>
            <div className="roha-admin-toolbar">
              <button type="submit" className="roha-admin-navbar__chip roha-admin-navbar__chip--primary" disabled={isSubmitting}>
                {isSubmitting ? "Adding..." : "Add jewellery"}
              </button>
            </div>
          </form>
        </AdminSurface>
      </AdminPageShell>
    </>
  );
};

export default AddProduct;
